<template>
  <d-modal
    :active="isActive"
    :width="700"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          {{ contentListing.name }}
        </h3>
        <p class="text-4xl font-bold">
          Promo Codes
        </p>
      </div>

      <!-- Add Promo Code Form -->
      <form @submit.prevent="savePromoCode" class="mb-6 space-y-4">
        <d-field label="Code">
          <d-input v-model="form.code" required />
        </d-field>
        <d-field label="Note (Optional)">
          <d-input v-model="form.note" />
        </d-field>
        <d-field label="Expiration Date (Optional)">
          <d-datepicker v-model="form.expirationDate" mode="date" />
        </d-field>
        <div class="flex justify-end">
          <d-btn type="submit" size="sm" :loading="saving">
            Add Promo Code
          </d-btn>
        </div>
      </form>

      <hr class="my-6">

      <!-- Promo Codes List -->
      <div class="flex-grow">
        <div v-if="loading" class="flex justify-center py-10">
          <d-loader />
        </div>
        <div v-else>
          <div v-if="promoCodes.length === 0">
            <p>There have been no promo codes created for this content listing</p>
          </div>
          <div v-else>
            <div v-for="promoCode in promoCodes" :key="promoCode.id" class="pb-5 relative" @mouseover="hoveredId = promoCode.id" @mouseleave="hoveredId = 0">
              <div>
                <p class="font-bold mb-0">
                  {{ promoCode.code }}
                </p>
                <p v-if="promoCode.expirationDate" class="mb-0">
                  Expiration Date: {{ promoCode.expirationDate | formatShortDate }}
                </p>
                <p v-if="promoCode.note" class="mb-0">
                  Note: {{ promoCode.note }}
                </p>
              </div>
              <transition name="fade">
                <div v-if="hoveredId === promoCode.id" class="ml-3 text-base absolute bg-white" style="top: 24%;right: 20px;">
                  <d-icon icon="trash" class="text-error p-2 cursor-pointer" @click="removePromoCode(promoCode)" />
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex justify-end mt-6">
        <d-pagination :total-items="total" :per-page="pagination.pageSize" v-model:current="pagination.page" />
      </div>

      <!-- Close Button -->
      <div class="flex justify-end mt-6">
        <d-btn 
          type="button"
          flat 
          @click="handleClose"
        >
          Close
        </d-btn>
      </div>
    </div>
  </d-modal>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { showErrorToast } from '@services/errorHandling.service';

export default {
  name: 'PromoCodeModal',
  
  props: {
    active: {
      type: Boolean,
      default: false
    },
    contentListing: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      form: {
        code: '',
        note: '',
        expirationDate: null,
      },
      defaultForm: {
        code: '',
        note: '',
        expirationDate: null,
      },
      hoveredId: 0,
      saving: false,
      promoCodes: [],
      loading: false,
      filter: '',
      total: 0,
      pagination: {
        page: 1,
        pageSize: 5,
        sortField: 'code',
        sortOrder: 'asc',
      },
    };
  },

  computed: {
    isActive() {
      return this.active;
    }
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.resetForm();
        this.getPaged();
      }
    },
    
    pagination: {
      handler() {
        if (this.active) {
          this.getPaged();
        }
      },
      deep: true,
    },
    
    filter() {
      if (this.active) {
        this.getDebouncedPaged();
      }
    },
  },

  methods: {
    resetForm() {
      this.form = JSON.parse(JSON.stringify(this.defaultForm));
    },

    // eslint-disable-next-line func-names
    getDebouncedPaged: debounce(function () {
      this.getPaged();
    }, 500),

    async getPaged() {
      if (!this.contentListing.id) return;
      
      const {
        page, sortField, sortOrder, pageSize,
      } = this.pagination;
      const params = [`filter=${this.filter}`, `sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`, `contentListingId=${this.contentListing.id}`].join('&');
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/promo-codes/paged?${params}`);
        this.promoCodes = data.resultItems;
        this.total = data.totalRecords;
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.loading = false;
      }
    },

    async savePromoCode() {
      this.saving = true;
      try {
        await axios.post(`/api/promo-codes?contentListingId=${this.contentListing.id}`, this.form);
        this.form = JSON.parse(JSON.stringify(this.defaultForm));
        this.$toast.success('Promo code was successfully created');
        await this.getPaged();
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.saving = false;
      }
    },

    async removePromoCode(promoCode) {
      try {
        await axios.delete(`/api/promo-codes/${promoCode.id}`);
        await this.getPaged();
      } catch (error) {
        showErrorToast(error);
      }
    },

    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Remove any potential duplicate styling that might cause ghost outlines */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>